import { styled } from 'css-literal-loader/styled';
import { navigate } from 'gatsby';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import withProps from 'recompose/withProps';
import mapProps from 'recompose/mapProps';

import paths from '../paths';
import withReservation from '../utils/Reservation';
import Button from './Button';
import Card from './Card';
import {
  CardBottom,
  CardCol,
  CardRow,
  PageSegmentContent,
} from './CrossSegmentCard';
import { CONTACT_URL } from '../config';
import FeatureListItem from './FeatureListItem';
import FormattedPrice from './FormattedPrice';
import PageSegment from './PageSegment';
import {
  BilledAnnually,
  DevicePriceFootnote,
  SingleDevicePriceFootnote,
} from './PriceFootnotes';
import Responsive from './Responsive';
import ResponsiveLineBreak from './ResponsiveLineBreak';
import Small from './Small';

import utilStyles from '../styles/utils.module.scss';

const applications = (
  <FormattedMessage
    defaultMessage="19 Clinical Applications"
    id="bundle.features.applications"
  />
);
const modes = (
  <FormattedMessage
    defaultMessage="B-mode, M-mode, Color Doppler"
    id="bundle.features.modes"
  />
);
const softwareUpdates = (
  <FormattedMessage
    defaultMessage="Unlimited Software Updates"
    id="bundle.features.softwareUpdates"
  />
);
const support = (
  <FormattedMessage
    defaultMessage="Premium Support"
    id="bundle.features.support"
  />
);
const cloudStorage = (
  <FormattedMessage
    defaultMessage="Unlimited HIPAA Compliant Storage"
    id="bundle.features.cloudStorage"
  />
);
const studySharing = (
  <FormattedMessage
    defaultMessage="De-identified Clip Sharing"
    id="bundle.features.studySharing"
  />
);
const sharedArchives = (
  <FormattedMessage
    defaultMessage="Shared Team Archives"
    id="bundle.features.sharedArchives"
  />
);
const commenting = (
  <FormattedMessage
    defaultMessage="Real-Time Commenting and notifications"
    id="bundle.features.commenting"
  />
);
const pacs = (
  <FormattedMessage
    defaultMessage="Push to PACS, VNA, Q-Path™"
    id="bundle.features.support"
  />
);
const worklists = (
  <FormattedMessage
    defaultMessage="Worklist Integration"
    id="bundle.features.worklists"
  />
);

const Strong = styled('strong', null, "Strong", require("./BundlePricingPageSegment-Strong.module.scss"), "strong", "strong");

const Footnote = styled('aside', null, "Footnote", require("./BundlePricingPageSegment-Footnote.module.scss"), "footnote", "footnote");

const SelectButton = styled(Button, null, "SelectButton", require("./BundlePricingPageSegment-SelectButton.module.scss"), "select-button", "selectButton");

const AdditionalPurchaseOptions = styled(Card, null, "AdditionalPurchaseOptions", require("./BundlePricingPageSegment-AdditionalPurchaseOptions.module.scss"), "additional-purchase-options", "additionalPurchaseOptions");

const CenteredCol = withProps({ justify: 'center', direction: 'column' })(
  styled(CardCol, null, "CenteredCol", require("./BundlePricingPageSegment-CenteredCol.module.scss"), "centered-col", "centeredCol"),
);

const Plus = withProps({ children: '+' })(Strong);

const Hr = styled('hr', null, "Hr", require("./BundlePricingPageSegment-Hr.module.scss"), "hr", "hr");

const EmptyCell = withProps({ role: 'cell' })(CardCol);

const FeatureCell = mapProps(p => ({
  role: 'cell',
  children: <FeatureListItem grow {...p} />,
}))(CardCol);

const warranty = (
  <Small>
    <FormattedMessage
      defaultMessage="1 year manufacturer’s warranty included"
      id="bundle.pricing.warranty"
    />
    <br />
    <Small muted>
      <FormattedMessage
        defaultMessage="extendable up to 3 years"
        id="bundle.pricing.warranty.extension"
      />
    </Small>
  </Small>
);

const propTypes = {};

const monthsPerYear = 12;

class BundlePricingPageSegment extends React.Component {
  updateCart(plan) {
    return this.props.cart.setPlan(plan);
  }

  async selectBundle(plan) {
    await this.updateCart(plan);
    navigate(paths.configure);
  }

  render() {
    const { cart } = this.props;
    const { maxCloudUsers } = cart;
    const { teamBaseNumCloudUsers } = cart.remote.inventory;

    return (
      <PageSegment theme="light">
        <PageSegmentContent>
          <CardBottom>
            <Card.Segment
              grow
              justify="space-around"
              direction="column"
              style={{ paddingBottom: 0 }}
            >
              <CardRow>
                <h3 className={utilStyles.srOnly}>
                  <FormattedMessage
                    defaultMessage="Individual"
                    id="bundle.pricing.individual.heading"
                  />
                </h3>
                <h3 className={utilStyles.srOnly}>
                  <FormattedMessage
                    defaultMessage="Team"
                    id="bundle.pricing.team.heading"
                  />
                </h3>
              </CardRow>

              <CardRow>
                <CenteredCol>
                  <Strong>
                    <FormattedPrice
                      value={
                        monthsPerYear *
                        cart.remote.inventory.cloudIndividualPricePerMonth
                      }
                      perYear
                    />
                  </Strong>
                </CenteredCol>
                <CenteredCol>
                  <Strong>
                    <FormattedPrice
                      value={monthsPerYear * cart.cloudTeamBasePricePerMonth}
                      perYear
                    />
                  </Strong>
                </CenteredCol>
              </CardRow>

              <CardRow>
                <CenteredCol align="center">
                  <BilledAnnually level="4" />
                </CenteredCol>
                <CenteredCol align="center">
                  <Small>
                    <FormattedMessage
                      id="bundle.team.usersNote"
                      defaultMessage="{teamBaseNumCloudUsers}+ users{rbr} (Includes first {teamBaseNumCloudUsers} users)"
                      values={{
                        teamBaseNumCloudUsers,
                        rbr: <ResponsiveLineBreak smDown />,
                      }}
                    />
                  </Small>
                  <BilledAnnually level="4" />
                </CenteredCol>
              </CardRow>
              <CardRow>
                <CenteredCol>
                  <Plus />
                </CenteredCol>
                <CenteredCol>
                  <Plus />
                </CenteredCol>
              </CardRow>
              <CardRow>
                <CenteredCol>
                  <Strong>
                    <FormattedPrice value={cart.devicePrice} noCents />
                  </Strong>
                </CenteredCol>
                <CenteredCol>
                  <Strong>
                    <FormattedPrice value={cart.devicePrice} noCents />
                  </Strong>
                </CenteredCol>
              </CardRow>

              <CardRow>
                <CenteredCol>
                  <SingleDevicePriceFootnote style={{ marginBottom: 10 }} />

                  {warranty}
                </CenteredCol>
                <CenteredCol>
                  <DevicePriceFootnote style={{ marginBottom: 10 }} />
                  {warranty}
                </CenteredCol>
              </CardRow>

              <CardRow>
                <CardCol direction="column">
                  <SelectButton
                    onClick={() => this.selectBundle('INDIVIDUAL')}
                  >
                    Select
                  </SelectButton>
                </CardCol>
                <CardCol direction="column">
                  <SelectButton onClick={() => this.selectBundle('TEAM')}>
                    Select
                  </SelectButton>
                </CardCol>
              </CardRow>

              <CardRow>
                <CardCol>
                  <Hr />
                </CardCol>
                <CardCol>
                  <Hr />
                </CardCol>
              </CardRow>
            </Card.Segment>
            <Responsive smDown>
              {isSmall => (
                <Card.Segment
                  grow
                  noBorder
                  role="table"
                  direction="column"
                  aria-label="Butterfly iQ bundle feature matrix"
                  style={{ paddingTop: 0 }}
                  pad={isSmall ? 6 : 4}
                >
                  <div className={utilStyles.srOnly} role="row">
                    <div role="columnheader">Individual bundle features</div>
                    <div role="columnheader">Team bundle features</div>
                  </div>
                  <CardRow role="row">
                    <FeatureCell>
                      <FormattedMessage
                        defaultMessage="1 User"
                        id="bundle.pricing.individualUser"
                      />
                    </FeatureCell>
                    <FeatureCell>
                      <FormattedMessage
                        defaultMessage="Includes First {teamBaseNumCloudUsers} Users"
                        id="bundle.pricing.individualUser"
                        values={{ teamBaseNumCloudUsers }}
                      />
                    </FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <FeatureCell>{applications}</FeatureCell>
                    <FeatureCell>{applications}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <FeatureCell>{modes}</FeatureCell>
                    <FeatureCell>{modes}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <FeatureCell>{softwareUpdates}</FeatureCell>
                    <FeatureCell>{softwareUpdates}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <FeatureCell>{support}</FeatureCell>
                    <FeatureCell>{support}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <FeatureCell>{cloudStorage}</FeatureCell>
                    <FeatureCell>{cloudStorage}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <FeatureCell>{studySharing}</FeatureCell>
                    <FeatureCell>{studySharing}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <EmptyCell />
                    <FeatureCell>{sharedArchives}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <EmptyCell />
                    <FeatureCell>{commenting}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <EmptyCell />
                    <FeatureCell>{pacs}</FeatureCell>
                  </CardRow>
                  <CardRow role="row">
                    <EmptyCell />
                    <FeatureCell>{worklists}</FeatureCell>
                  </CardRow>
                </Card.Segment>
              )}
            </Responsive>
          </CardBottom>
          <AdditionalPurchaseOptions>
            <Card.Segment justify="center">
              <FormattedMessage
                defaultMessage="For additional purchase options:&nbsp;{link}"
                id="bundle.additionalPurchaseOptions"
                values={{
                  link: (
                    <a href={CONTACT_URL}>
                      <FormattedMessage
                        id="bundle.contactUs"
                        defaultMessage="Contact us"
                      />
                    </a>
                  ),
                }}
              />
            </Card.Segment>
          </AdditionalPurchaseOptions>
          <Footnote id="pricing-footnote">
            <FormattedMessage
              tagName="p"
              defaultMessage="Contact us for special pricing on teams over {maxCloudUsers} users"
              id="bundle.pricing.footnote-3"
              values={{ maxCloudUsers: maxCloudUsers + 1 }}
            />
          </Footnote>
        </PageSegmentContent>
      </PageSegment>
    );
  }
}
BundlePricingPageSegment.propTypes = propTypes;

export default withReservation(BundlePricingPageSegment);
